import React, { useContext, useRef, useState } from 'react';
import styles from './UploadServicesInBulk.module.css';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { FaFileExcel } from 'react-icons/fa';
import { BASE_URL_API } from '../../References/URLs';
import AuthContext from '../../store/auth-context';
import ActiveStoreContext from '../../store/ActiveStoreContext';
import { FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';

const REQUIRED_HEADERS = ["serviceName", "basePrice"];
// behavior toggles
const EXACT_ORDER = true;          // require "serviceName,basePrice" in that order
const ERROR_ON_DUPLICATE_NAMES = true;
const MAX_NAME_LEN = 100;
const MAX_ROWS = 25000;            // safety cap; tune for your needs
const MAX_FILE_SIZE_MB = 10;
// strip BOM/zero-width/NBSP and trim
const clean = (s) =>
    String(s ?? "")
        .replace(/\uFEFF/g, "")   // BOM
        .replace(/\u200B/g, "")   // zero-width
        .replace(/\u00A0/g, " ")  // NBSP -> space
        .trim();

// normalize header *for comparison only* (we keep original for display)
const normalizeHeaderStrict = (h) => clean(h);             // strict: only trim
const normalizeHeaderLoose = (h) => clean(h);

// parse price like "₹1,499.50" or "1,499.50" -> 1499.5
const parsePrice = (v) => {
    if (v === null || v === undefined) return { ok: false, val: null };
    const s = clean(v);
    if (s === "") return { ok: false, val: null };
    // remove currency symbols and spaces; keep digits and dot
    const num = s.replace(/[^\d.]/g, "");
    if (num === "") return { ok: false, val: null };
    // guard multiple dots (e.g., "1.234.56")
    if ((num.match(/\./g) || []).length > 1) return { ok: false, val: null };
    const val = Number(num);
    if (!Number.isFinite(val) || val < 0) return { ok: false, val: null };
    return { ok: true, val };
};

const UploadServicesInBulk = ({ activeSection, categoryName, categoryIdentifier, onClose, getServices }) => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = (() => { try { return (asCtx?.activeStore && asCtx.activeStore.trim() !== "") ? JSON.parse(asCtx.activeStore) : null; } catch { return null; } })();
    const [fileData, setFileData] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [error, setError] = useState("");
    const fileInputRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false)

    const triggerFileSelect = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // Download sample sheet
    const downloadSampleSheet = () => {
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet([REQUIRED_HEADERS]);
        XLSX.utils.book_append_sheet(wb, ws, "Sample");
        XLSX.writeFile(wb, "sample_services_upload.xlsx");
    };

    // Validate headers
    // const validateHeaders = (fileHeaders) => {
    //     if (fileHeaders.length !== REQUIRED_HEADERS.length) return false;
    //     return REQUIRED_HEADERS.every((h, i) => h === fileHeaders[i]);
    // };
    const validateHeaders = (rawHeaders) => {
        // Trim & strip invisibles from each header coming from file
        const headers = rawHeaders.map(normalizeHeaderStrict);

        // reject empties and Excel "__EMPTY" headers if they have any data under them later
        if (headers.some(h => h === "" || /^__EMPTY/i.test(h))) {
            return { ok: false, reason: "One or more header names are empty/invalid." };
        }

        // must be exactly 2 headers
        if (headers.length !== REQUIRED_HEADERS.length) {
            return { ok: false, reason: `Expected exactly ${REQUIRED_HEADERS.join(", ")}, found ${headers.length} columns.` };
        }

        // no duplicates
        const unique = new Set(headers);
        if (unique.size !== headers.length) {
            return { ok: false, reason: "Duplicate header names detected." };
        }

        // exact match (trimmed) and (optionally) exact order
        const expected = REQUIRED_HEADERS.map(normalizeHeaderStrict);

        if (EXACT_ORDER) {
            const sameOrder = headers.every((h, i) => h === expected[i]);
            if (!sameOrder) {
                return { ok: false, reason: `Invalid header order. Expected: ${REQUIRED_HEADERS.join(", ")}` };
            }
        } else {
            const sameSet = headers.every(h => expected.includes(h));
            if (!sameSet) {
                return { ok: false, reason: `Invalid headers. Expected exactly: ${REQUIRED_HEADERS.join(", ")}` };
            }
        }

        return { ok: true, headers };
    };

    const processData = (data) => {
        if (!Array.isArray(data) || data.length === 0) {
            setError("No data found in the file.");
            setFileData([]);
            setHeaders([]);
            return;
        }

        // Derive raw headers from first row keys
        const rawHeaders = Object.keys(data[0]);
        const hv = validateHeaders(rawHeaders);

        if (!hv.ok) {
            setError(`Invalid file headers. ${hv.reason}`);
            setFileData([]);
            setHeaders([]);
            return;
        }

        // Canonical headers for display (trimmed)
        const displayHeaders = rawHeaders.map(h => clean(h));

        // Enforce max rows to avoid accidental huge uploads
        if (data.length > MAX_ROWS) {
            setError(`Too many rows (${data.length}). Limit is ${MAX_ROWS}.`);
            setFileData([]);
            setHeaders([]);
            return;
        }

        // Clean/validate each row
        const errors = [];
        const warnings = [];
        const seenNames = new Set();

        const cleanedRows = data
            .map((row, idx) => {
                // idx is 0-based data row (header is above); for user-facing line numbers add +2
                const line = idx + 1;

                const nameRaw = row[rawHeaders[0]];
                const priceRaw = row[rawHeaders[1]];

                const serviceName = clean(nameRaw);
                const priceParsed = parsePrice(priceRaw);

                // skip rows that are entirely blank
                if (serviceName === "" && clean(priceRaw) === "") return null;

                // validations
                if (serviceName === "") {
                    errors.push(`Row ${line}: serviceName is required.`);
                } else {
                    if (serviceName.length > MAX_NAME_LEN) {
                        errors.push(`Row ${line}: serviceName exceeds ${MAX_NAME_LEN} characters.`);
                    }
                    if (ERROR_ON_DUPLICATE_NAMES) {
                        if (seenNames.has(serviceName.toLowerCase())) {
                            errors.push(`Row ${line}: Duplicate serviceName "${serviceName}".`);
                        } else {
                            seenNames.add(serviceName.toLowerCase());
                        }
                    }
                }

                if (!priceParsed.ok) {
                    errors.push(`Row ${line}: basePrice is missing or invalid.`);
                }

                return {
                    serviceName,
                    basePrice: priceParsed.ok ? priceParsed.val : null,
                    __invalid__: {
                        serviceName: serviceName === "",
                        basePrice: !priceParsed.ok
                    }
                };
            })
            .filter(Boolean);

        if (cleanedRows.length === 0) {
            setError("All rows are blank.");
            setFileData([]);
            setHeaders([]);
            return;
        }

        // Show errors (limit noise)
        if (errors.length > 0) {
            setError(errors)
            // setError(`Found ${errors.length} issue(s). First few: \n• ${errors.slice(0, 8).join("\n• ")}`);
        } else {
            setError("");
        }

        setHeaders(displayHeaders);
        setFileData(cleanedRows);
    };

    // CSV
    const handleCsv = (file) => {
        Papa.parse(file, {
            header: true,
            skipEmptyLines: "greedy",
            worker: true,
            complete: (res) => {
                // Post-process headers
                const cleanedData = res.data.map(row =>
                    Object.fromEntries(
                        Object.entries(row).map(([k, v]) => [clean(k), typeof v === "string" ? clean(v) : v])
                    )
                );
                processData(cleanedData);
            },
            error: () => setError("Failed to parse CSV. Please check the file."),
        });
    };

    // Excel
    const handleXlsx = (file) => {
        const reader = new FileReader();
        reader.onload = (evt) => {
            const bstr = evt.target.result;
            const wb = XLSX.read(bstr, { type: "binary" });

            // pick first non-empty sheet
            const sheetName = wb.SheetNames.find((n) => {
                const ws = wb.Sheets[n];
                return ws && ws["!ref"];
            });
            if (!sheetName) {
                setError("No readable sheet found in workbook.");
                return;
            }

            const ws = wb.Sheets[sheetName];

            // Convert to JSON with header row; keep empties as ""
            const jsonData = XLSX.utils.sheet_to_json(ws, {
                defval: "",
                raw: false // ensures numeric-looking strings stay strings (we parse ourselves)
            });

            // As a guard against Excel's __EMPTY headers: if headers look empty, call it out early
            if (jsonData[0] && Object.keys(jsonData[0]).some(k => k === "" || /^__EMPTY/.test(k))) {
                setError("Invalid or empty header cells detected. Please ensure the first row has exactly: serviceName, basePrice.");
                setFileData([]);
                setHeaders([]);
                return;
            }

            processData(jsonData);
        };
        reader.readAsBinaryString(file);
    };

    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const sizeMb = file.size / (1024 * 1024);
        if (sizeMb > MAX_FILE_SIZE_MB) {
            setError(`File is too large (${sizeMb.toFixed(1)} MB). Max allowed is ${MAX_FILE_SIZE_MB} MB.`);
            return;
        }

        const ext = file.name.split(".").pop()?.toLowerCase();
        if (ext === "csv") return handleCsv(file);
        if (ext === "xlsx" || ext === "xls") return handleXlsx(file);

        setError("Please upload a CSV or Excel (.xlsx/.xls) file.");
    };


    const uploadInBulk = async () => {
        if (window.confirm(`Are you sure to upload ${fileData?.length} service(s) in bulk?`)) {
            setIsLoading(true)
            const response = await fetch(BASE_URL_API + "addServicesInBulk",
                {
                    method: "POST",
                    body: JSON.stringify({
                        token: authCtx.token,
                        storeIdentifier: activeStore?.storeIdentifier,
                        data: fileData,
                        categoryName: categoryName,
                        categoryIdentifier: categoryIdentifier,
                        sectionIdentifier: activeSection,
                        storeName: activeStore?.storeName
                    })
                });

            if (!response.ok) {
                console.log("Something went wrong : Server Error")
            } else {
                const respo = await response.json()
                if (respo?.status === "success") {
                    toast('Price list successfully uploaded!')
                    getServices()
                    onClose()
                } else {
                    if (respo?.message === "tokenExpired") {
                        authCtx.logout()
                    }
                }
                console.log(respo)
            }
            setIsLoading(false)
        }
        console.log(fileData)
    }


    return (
        <div className={styles.uploadInBulkWrapper}>
            <div className={styles.uibwContainer}>
                <div className={styles.uibwContent}>
                    <div className={styles.mainHeader}>
                        <div className={styles.headerTxt}>
                            <div className={styles.title}>
                                Add services in bulk under
                            </div>
                            <div className={styles.categoryName}>
                                {categoryName}
                            </div>
                        </div>
                        <div>
                            <button onClick={onClose} disabled={isLoading} className={styles.closeBtn}>Close</button>
                        </div>
                    </div>
                    {fileData?.length < 1 &&
                        <div className={styles.initialStateWrapper} >
                            <div className={styles.initialStateContainer}>
                                <div className={styles.initialStateHeader}>
                                    Upload Excel / CSV file
                                </div>
                                <div className={styles.initialStateBody}>
                                    {/* Download sample sheet */}
                                    <div className={styles.downloadBox}>
                                        <button onClick={downloadSampleSheet} className={styles.sampleBtn}>
                                            <FaFileExcel size={40} />
                                            Download Sample Sheet
                                        </button>
                                    </div>

                                    {/* Upload file */}
                                    <div className={styles.uploadBox}>
                                        <input
                                            type="file"
                                            accept=".csv, .xlsx, .xls"
                                            onChange={handleFileUpload}
                                            ref={fileInputRef}
                                            style={{ display: 'none' }}
                                        />
                                    </div>
                                </div>
                                <div className={styles.initialStateFooter} >
                                    <a href="tel:+91-9999648406" target="_blank" className={styles.helpCenterTxt}>
                                        ? Help Center
                                    </a>
                                    <div className={styles.actionWrapper}>
                                        <button className={styles.cancelBtn} onClick={() => onClose()} >Cancel</button>
                                        <button className={styles.importBtn} onClick={() => triggerFileSelect()} >Import</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }


                    {fileData.length > 0 && (
                        <div className={styles.mainBodyWrapper} >
                            <div className={styles.previewTableWrapper}>
                                <table className={styles.previewTable}>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            {headers.map((head, idx) => <th key={idx}>{head}</th>)}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {fileData.slice(0, 1000).map((row, i) => (
                                            <tr key={i}>
                                                <td>{i + 1}</td>
                                                <td className={row.__invalid__?.serviceName ? styles.invalidCell : ""}>
                                                    {row.serviceName}
                                                </td>
                                                <td className={row.__invalid__?.basePrice ? styles.invalidCell : ""}>
                                                    {row.basePrice ?? ""}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                            </div>

                            <div className={styles.nextStepWrapper}>
                                {error?.length > 0 &&
                                    <div className={styles.errorWrapper}>
                                        <p className={styles.error}>Found some errors!</p>
                                        <ul className={styles.errors}>
                                            {error?.map((item, index) => (
                                                <li key={index}>
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                }
                                <div className={styles.nextStepBtn} >
                                    <button onClick={() => setFileData([])} disabled={isLoading} className={styles.cancelBtnNs} >Cancel</button>
                                    {error?.length > 0 ?
                                        <button onClick={() => alert(`Kindly fix all the errors to upload!`)} className={styles.uploadBtnNs} >Upload</button>
                                        :
                                        <>
                                            {isLoading === true ?
                                                <button className={styles.uploadBtnNsLoader} ><FaSpinner size={16} className={styles.spinner} /> Uploading...</button>
                                                :
                                                <button onClick={() => uploadInBulk()} className={styles.uploadBtnNs} >Upload</button>
                                            }
                                        </>
                                    }
                                </div>
                            </div>

                        </div>
                    )}

                    {/* <div className={styles.footerWrapper}>
                        <p className={styles.note}>Showing first 1000 rows for preview</p>
                    </div> */}

                </div>
            </div>
        </div>
    );
};

export default UploadServicesInBulk;
